import React, { useEffect, useState } from 'react';
import { Trophy, TrendingUp, Target, ChartBar } from 'lucide-react';

/**
 * AI Track Record public banner.
 * Only renders when the backend reports eligible=true (>=30 evaluated ideas AND
 * overall win_rate_pct >= 70). Otherwise renders nothing.
 */
const AITrackRecordSection = ({ API_URL }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/ai-track-record`);
        const json = await res.json();
        if (!cancelled && json && json.eligible) setData(json);
      } catch (_e) { /* silent — don't block landing page render */ }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);

  if (!data || !data.eligible) return null;

  const segLabels = {
    index_options: 'Index Options',
    stock_fno: 'Stock F&O',
    equity: 'Equity Cash',
  };

  return (
    <section id="ai-track-record" className="py-16 px-4 bg-gradient-to-b from-gray-900/30 to-black/50" data-testid="ai-track-record-section">
      <div className="max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-golden/10 border border-golden/30 px-3 py-1 rounded-full mb-4">
          <Trophy className="w-4 h-4 text-golden" />
          <span className="text-golden text-xs font-semibold uppercase tracking-wider">Proven AI Track Record</span>
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3">
          <span className="text-golden">{data.win_rate_pct}%</span> Win Rate
        </h2>
        <p className="text-gray-400 text-sm mb-8">
          Across <span className="font-semibold text-white">{data.total_ideas}</span> AI-generated trade ideas over the last {data.window_days} days · independently verified against actual market OHLC.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div className="bg-black/60 border border-golden/20 rounded-lg p-4">
            <Target className="w-6 h-6 text-golden mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{data.total_ideas}</p>
            <p className="text-xs text-gray-400 mt-1">Total Ideas</p>
          </div>
          <div className="bg-black/60 border border-emerald-500/20 rounded-lg p-4">
            <TrendingUp className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-400">{data.win_rate_pct}%</p>
            <p className="text-xs text-gray-400 mt-1">Overall Win Rate</p>
          </div>
          <div className="bg-black/60 border border-blue-500/20 rounded-lg p-4">
            <ChartBar className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-400">{data.t2_hit_pct}%</p>
            <p className="text-xs text-gray-400 mt-1">T2 Hit Rate</p>
          </div>
          <div className="bg-black/60 border border-purple-500/20 rounded-lg p-4">
            <Trophy className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-400">{data.window_days}d</p>
            <p className="text-xs text-gray-400 mt-1">Track Window</p>
          </div>
        </div>

        {data.by_segment_win_rates && (
          <div className="mt-6 max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {Object.entries(data.by_segment_win_rates).map(([seg, rate]) => rate != null && (
              <div key={seg} className="bg-black/40 border border-white/10 rounded-md p-3">
                <p className="text-gray-500 text-xs">{segLabels[seg] || seg}</p>
                <p className={`text-lg font-bold mt-0.5 ${rate >= 70 ? 'text-emerald-400' : rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{rate}%</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-6 italic">
          * Educational analysis only. Past performance is not indicative of future results.
        </p>
      </div>
    </section>
  );
};

export default AITrackRecordSection;
